import { useState } from 'react';
import { APIStates, ProductStates } from './product-state-machine-types';
import { usePostQueryDecorator } from './use-post-query-decorator';
import { usePreQueryDecorator } from './use-pre-query-decorator';
import { useInvocations } from './invocations/use-invocations';
import { type ResultSuppliers } from '../product-router';
import { type CacheWarmingProps } from '../product-router/product';
import { useQuery } from '../query-context';
import { type SearchItems } from '../product-router/product/result-types';
import { useAppliedFilters } from '../filters';
import { type Workspace } from '../../common/search-config-provider';
import { useSearchSessionId } from '../../common/search-session-provider';
import { useFeaturesContext } from '../features-context/features-context';

export const ProductStateMachinePossibleStates = {
	preQuery: {
		[APIStates.Typing]: ProductStates.Invalid,
		[APIStates.Error]: ProductStates.PreQueryError,
		[APIStates.Loading]: ProductStates.PreQueryLoading,
		[APIStates.NoResult]: ProductStates.PreQueryNoResult,
		[APIStates.Success]: ProductStates.PreQuerySuccess,
	},
	postQuery: {
		[APIStates.Typing]: ProductStates.PostQueryTyping,
		[APIStates.Error]: ProductStates.PostQueryError,
		[APIStates.Loading]: ProductStates.PostQueryLoading,
		[APIStates.NoResult]: ProductStates.PostQueryNoResult,
		[APIStates.Success]: ProductStates.PostQuerySuccess,
	},
};

export interface StateMachineArgs {
	setPreQueryItems: (items: SearchItems) => void;
	setPostQueryItems: (items: SearchItems) => void;
	id: string;
	debounceTime?: number;
	onRetry?: () => void;
	isPreQueryFiltersEnabled?: boolean;
}

export type ProductStateMachineArgs = StateMachineArgs &
	ResultSuppliers &
	CacheWarmingProps & {
		workspaces?: Workspace[];
	};

export const useProductStateMachine = ({
	setPreQueryItems,
	setPostQueryItems,
	preQueryItemSupplier,
	postQueryItemSupplier,
	id,
	isPrefetchingEnabled = false,
	debounceTime = 250,
	onRetry,
	workspaces,
	isPreQueryFiltersEnabled = false,
}: ProductStateMachineArgs) => {
	const { query, queryVersion } = useQuery();
	const searchSessionId = useSearchSessionId();
	const appliedFilters = useAppliedFilters(id);
	const features = useFeaturesContext();

	/**
	 * We track the API calls of Post and Pre query separately.
	 */
	const [preQueryAPIState, setPreQueryAPIState] = useState<APIStates>(APIStates.Loading);
	const [postQueryAPIState, setPostQueryAPIState] = useState<APIStates>(APIStates.Loading);

	const decoratedPreQuerySupplier = usePreQueryDecorator({
		setAPIState: setPreQueryAPIState,
		preQueryItemSupplier,
		setState: setPreQueryItems,
		id,
	});

	const decoratedPostQuerySupplier = usePostQueryDecorator({
		setAPIState: setPostQueryAPIState,
		setState: setPostQueryItems,
		postQueryItemSupplier,
		debounceTime,
		id,
	});

	const {
		onRetryPreQuery: updateStateOnRetryPreQuery,
		onRetryPostQuery: updateStateOnRetryPostQuery,
	} = useInvocations({
		id,
		query,
		analyticsInfo: {
			queryVersion,
			searchSessionId,
		},
		filters: appliedFilters,
		isPrefetchingEnabled,
		isPreQueryFiltersEnabled,
		preQueryItemSupplier: decoratedPreQuerySupplier,
		postQueryItemSupplier: decoratedPostQuerySupplier,
		debounceTime,
		setPostQueryItems,
		onRetry,
		setPostQueryAPIState,
		workspaces,
	});

	let state: ProductStates;

	if (
		(query !== '' || (isPreQueryFiltersEnabled && appliedFilters.length)) &&
		!features?.quickFind?.postQueryDisabled
	) {
		state = ProductStateMachinePossibleStates.postQuery[postQueryAPIState];
	} else {
		state = ProductStateMachinePossibleStates.preQuery[preQueryAPIState];
	}

	return {
		state,
		onRetryPreQuery: updateStateOnRetryPreQuery,
		onRetryPostQuery: updateStateOnRetryPostQuery,
	};
};
